// Comments panel for the task page (T23, spec F4). The task page (lane D, T21) mounts it with the
// task id; people without comment.write see the thread but no form (the API refuses them anyway).
import { useState } from 'react';
import { COMMENTS_PAGE_SIZE, useTaskComments } from '../../api/comments.ts';
import { useMe } from '../../api/me.ts';
import { can } from '../../shell/nav.ts';
import { userMessage } from '../../ui/error-message.ts';
import { Skeleton } from '../../ui/skeleton.tsx';
import { CommentForm } from './comment-form.tsx';
import { CommentItem } from './comment-item.tsx';
import './comments.css';

export function CommentsPanel(props: { readonly taskId: string }): React.JSX.Element {
  const me = useMe();
  const [page, setPage] = useState(1);
  const thread = useTaskComments(props.taskId, page);
  const pages = Math.max(1, Math.ceil((thread.data?.total ?? 0) / COMMENTS_PAGE_SIZE));
  return (
    <section className="comments" aria-labelledby="comments-title">
      <h2 id="comments-title" className="comments__title">
        Comments
      </h2>
      {thread.isPending ? <Skeleton lines={2} label="Loading comments…" /> : null}
      {thread.isError ? (
        <p className="comments__error" role="alert">
          {userMessage(thread.error)}
        </p>
      ) : null}
      {thread.data?.total === 0 ? <p className="comments__empty">No comments yet.</p> : null}
      {thread.data !== undefined && thread.data.items.length > 0 ? (
        <ol className="comments__list">
          {thread.data.items.map((c) => (
            <CommentItem key={c.id} taskId={props.taskId} comment={c} />
          ))}
        </ol>
      ) : null}
      {pages > 1 ? (
        <nav className="comments__pager" aria-label="Comment pages">
          <button
            type="button"
            className="btn btn--secondary"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Earlier
          </button>
          <span className="comments__page">
            Page {page} of {pages}
          </span>
          <button
            type="button"
            className="btn btn--secondary"
            disabled={page >= pages}
            onClick={() => setPage(page + 1)}
          >
            Later
          </button>
        </nav>
      ) : null}
      {can(me, 'comment.write') ? <CommentForm taskId={props.taskId} /> : null}
    </section>
  );
}
